import "@taprootio/espalier/avatar";
import "@taprootio/espalier/box";
import "@taprootio/espalier/breadcrumbs";
import "@taprootio/espalier/button";
import "@taprootio/espalier/footer";
import "@taprootio/espalier/header";
import "@taprootio/espalier/image";
import "@taprootio/espalier/info";
import "@taprootio/espalier/menu";
import "@taprootio/espalier/menu/item";
import "@taprootio/espalier/page";
import "@taprootio/espalier/root";
import "@taprootio/espalier/section";
import { getEspBus } from "@taprootio/espalier/shared/bus-events";
import { EspalierElementBase } from "@taprootio/espalier/shared/element-base";
import { ESP_EVENTS } from "@taprootio/espalier/shared/events";
import { showToast } from "@taprootio/espalier/shared/toast-events";

import { css, html, LitElement, nothing, svg } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { loadIntegrationDataDocument } from "./integration-data.js";
import { loadRuntimeCapabilities } from "./runtime-capabilities/registry.js";
import { PUBLISHED_IMAGE_LAYOUT_SELECTOR } from "./shared/tiptap-image-layout-selector.js";
import { type TaprootSharedRuntime } from "./taproot-shared-runtime-access.js";
import { TAPROOT_SHARED_RUNTIME_API_VERSION } from "./taproot-shared-runtime-contract.js";

declare global {
  // eslint-disable-next-line no-var
  var __taprootSharedRuntime: TaprootSharedRuntime | undefined;
}

// Writes the data-overflowing state consumed by the table overflow cue rules
// in generator/src/templates/page.vto; the attribute alone drives them at
// every viewport. Published pages rely on both halves.
function installTableOverflowIndicators(): void {
  if (typeof document === "undefined") return;

  const selector = ".taproot-table-scroll";
  const regions = [...document.querySelectorAll<HTMLElement>(selector)];
  if (regions.length === 0) return;
  const update = (region: HTMLElement) => {
    const table = region.querySelector<HTMLElement>(":scope > table");
    const next = String(
      table !== null && table.scrollWidth > region.clientWidth + 1,
    );
    if (region.dataset.overflowing !== next) region.dataset.overflowing = next;
  };

  for (const region of regions) update(region);
  if (typeof ResizeObserver === "undefined") {
    window.addEventListener("resize", () => {
      for (const region of regions) update(region);
    });
    return;
  }

  const observer = new ResizeObserver((entries) => {
    const changedRegions = new Set<HTMLElement>();
    for (const entry of entries) {
      const target = entry.target;
      if (!(target instanceof HTMLElement)) continue;
      const region = target.matches(selector) ? target : target.closest<HTMLElement>(selector);
      if (region) changedRegions.add(region);
    }
    for (const region of changedRegions) update(region);
  });
  for (const region of regions) {
    observer.observe(region);
    const table = region.querySelector(":scope > table");
    if (table) observer.observe(table);
  }
}

const taprootSharedRuntime: TaprootSharedRuntime = {
  apiVersion: TAPROOT_SHARED_RUNTIME_API_VERSION,
  LitElement,
  css,
  html,
  nothing,
  svg,
  customElement,
  property,
  state,
  ESP_EVENTS,
  EspalierElementBase,
  getEspBus,
  showToast,
  loadIntegrationDataDocument,
  loadRuntimeCapabilities,
};

globalThis.__taprootSharedRuntime = taprootSharedRuntime;
installTableOverflowIndicators();
// The cheap presence check runs synchronously so the layout module itself —
// heavy enough to matter for the transfer budget — is fetched only on pages
// that actually have a target for it.
const publishedImageLayoutRoot = typeof document !== "undefined" ? document.body : null;
if (publishedImageLayoutRoot?.querySelector(PUBLISHED_IMAGE_LAYOUT_SELECTOR)) {
  void Promise.all([
    import("./shared/tiptap-image-layout.js"),
    import("../src/tiptap-prosemirror.js"),
  ]).then(([{ installPublishedImageLayout }, { TIPTAP_IMAGE_NARROW_CONTENT_REM }]) =>
    installPublishedImageLayout(publishedImageLayoutRoot, TIPTAP_IMAGE_NARROW_CONTENT_REM)
  );
}

export { taprootSharedRuntime };
